import type { BlockField } from '@/blocks/domain/types';
import type { HeaderSection, SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';
import type { SignatureContent } from '@/blocks/domain/types/BlockContent/SignatureContent';
import { defaultFieldsFor } from './defaultFieldsFor';

/** Fixed ids: the editor builds its draft AND its clean baseline from the same content, so a random id would read as an unsaved edit. */
export const SIGNATURE_STATEMENT_SECTION_ID = 'sec-signature-statement';
export const SIGNATURE_LINES_SECTION_ID = 'sec-signature-lines';

const LAYOUT_OF_COLUMN_COUNT: Record<number, SectionLayout> = { 1: '1', 2: '2', 3: '3' };

/**
 * The sections a set of default fields sit in, in order of first appearance, each as wide as the
 * highest column its fields use (1 / 2 / 3 columns). `null` when no field names a section - fields
 * a host registered in the legacy flat shape keep the flat signature layout.
 */
export function defaultSignatureSections(
  fields: ReadonlyArray<{ sectionId?: string; column?: number }> | undefined
): HeaderSection[] | null {
  const widest = new Map<string, number>();
  for (const field of fields ?? []) {
    if (!field.sectionId) continue;
    widest.set(field.sectionId, Math.max(widest.get(field.sectionId) ?? 0, typeof field.column === 'number' ? field.column : 0));
  }
  if (widest.size === 0) return null;
  return [...widest.entries()].map(([id, column]) => ({ id, layout: LAYOUT_OF_COLUMN_COUNT[Math.min(column + 1, 3)] ?? '1' }));
}

/** A fresh Signature block: the statement Text above two signature lines side by side, twice (Figma `be.signature.editor`). */
export function defaultSignatureContent(documentTypeKey?: string | null): SignatureContent {
  const fields: BlockField[] = defaultFieldsFor('signature', documentTypeKey);
  const sections = defaultSignatureSections(fields);
  return sections ? { kind: 'signature', fields, sections } : { kind: 'signature', fields };
}
